import { useContext, useEffect, useState } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'

const UNIT_MS = { d: 86400000, h: 3600000, m: 60000, s: 1000 }

/**
 * Splits the time left until `target` into the requested units, largest first
 * (`d`, `h`, `m`, `s`). Past the target, every unit is 0.
 */
const remaining = (target, keys, now) => {
  let left = Math.max(0, target - now)
  const out = {}
  for (const key of ['d', 'h', 'm', 's']) {
    if (!keys.includes(key)) continue
    out[key] = Math.floor(left / UNIT_MS[key])
    left -= out[key] * UNIT_MS[key]
  }
  return out
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/**
 * ReactiveJSON primitive — a live countdown to a date (days, hours, minutes,
 * seconds). reactive-json has no clock and no date arithmetic: a ticking value
 * cannot be expressed in YAML, hence this generic primitive.
 *
 * Auto-updating content (WCAG 2.2.2): for visitors who ask for reduced motion
 * the seconds are not rendered and the rest ticks once a minute. The element is
 * a `timer` with `aria-live="off"` (no flood of announcements); its accessible
 * name is `attributes.aria-label`, or the target date when none is given.
 * Once the date has passed it carries `data-ended="true"`, which the block's
 * CSS (or a sibling) can use to hide it or show something else.
 *
 * On a generated (SSG) page the block is an island woken only once the visitor
 * engages (classifyBlock): until then the islands LOADER keeps the digits live
 * without the engine, from `data-orbit-countdown` (the target) and
 * `data-unit` (each number) — see `src/site/islands.jsx`.
 *
 * YAML usage:
 *   - type: Countdown
 *     to: ~~.openingDate          # ISO date, e.g. "2027-05-15T10:00:00+02:00"
 *     units:                      # order = display order; label may be data
 *       - { key: d, label: jours }
 *       - { key: h, label: heures }
 *     itemClass: my-cd-item       # one wrapper per unit
 *     numberClass: my-cd-number
 *     labelClass: my-cd-label
 *     attributes: { class: my-cd, aria-label: "Ouverture le 15 mai 2027" }
 */
export const Countdown = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const to = resolve(props.to) ?? ''
  const target = Date.parse(to)
  const [reduced, setReduced] = useState(false)
  const units = (props.units ?? [])
    .filter((u) => !(reduced && u.key === 's'))
    .map((u) => ({ key: u.key, label: resolve(u.label) }))
  const keys = units.map((u) => u.key)
  const ticksSeconds = keys.includes('s')
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes ?? {})

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    setReduced(prefersReducedMotion())
  }, [])
  useEffect(() => {
    if (Number.isNaN(target)) return undefined
    const id = setInterval(() => setNow(Date.now()), ticksSeconds ? UNIT_MS.s : UNIT_MS.m)
    return () => clearInterval(id)
  }, [target, ticksSeconds])

  if (Number.isNaN(target)) return null
  const values = remaining(target, keys, now)
  const ended = now >= target
  const Tag = props.tag ?? 'div'
  const label = evaluatedAttrs['aria-label']
    || new Date(target).toLocaleDateString(document.documentElement.lang || undefined, { dateStyle: 'long' })

  return (
    <ActionDependant {...props}>
      <Tag
        role="timer"
        aria-live="off"
        {...evaluatedAttrs}
        aria-label={label}
        data-ended={ended ? 'true' : undefined}
        data-orbit-countdown={new Date(target).toISOString()}
      >
        {units.map((u) => (
          <span key={u.key} className={props.itemClass}>
            <span className={props.numberClass} data-unit={u.key}>{values[u.key]}</span>
            <span className={props.labelClass}>{u.label}</span>
          </span>
        ))}
      </Tag>
    </ActionDependant>
  )
}
